import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileText, Download, Lock, Loader2, Upload, Trash2, Eye, EyeOff, BookOpen } from "lucide-react";

const Field = ({ label, ...props }) => (
  <label className="flex flex-col gap-1 text-xs text-slate-400">
    {label}
    <input {...props} className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white focus:border-emerald-500 focus:outline-none" />
  </label>
);

const AdminUpload = ({ onDone }) => {
  const [form, setForm] = useState({ titulo: "", descricao: "", tipo_concurso: "", banca: "CEBRASPE", concurso: "", disciplina: "", assunto: "", premium: false });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!file) { toast.error("Selecione um PDF."); return; }
    if (!form.titulo.trim()) { toast.error("Informe o título."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await api.post("/admin/materials", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Material enviado!");
      setFile(null); setForm((f) => ({ ...f, titulo: "", descricao: "" }));
      onDone();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Falha no upload.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="materiais-admin-upload" className="rounded-2xl border border-amber-500/30 bg-slate-900 p-5">
      <div className="mb-3 flex items-center gap-2 text-amber-400"><Upload className="h-5 w-5" /><span className="font-bold">Enviar material (Admin)</span></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Título" value={form.titulo} onChange={set("titulo")} data-testid="mat-titulo" />
        <Field label="Descrição" value={form.descricao} onChange={set("descricao")} />
        <Field label="Tipo de concurso" value={form.tipo_concurso} onChange={set("tipo_concurso")} placeholder="Ex: Polícia Federal" />
        <label className="flex flex-col gap-1 text-xs text-slate-400">Banca
          <select value={form.banca} onChange={set("banca")} className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white">
            <option>CEBRASPE</option><option>CESGRANRIO</option>
          </select>
        </label>
        <Field label="Concurso/Órgão" value={form.concurso} onChange={set("concurso")} />
        <Field label="Disciplina" value={form.disciplina} onChange={set("disciplina")} />
        <Field label="Assunto" value={form.assunto} onChange={set("assunto")} />
        <label className="flex items-center gap-2 text-xs text-slate-300 sm:col-span-2">
          <input type="checkbox" checked={form.premium} onChange={(e) => setForm((f) => ({ ...f, premium: e.target.checked }))} /> Exclusivo Premium
        </label>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <input type="file" accept="application/pdf" data-testid="mat-file" onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-xs text-slate-400 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-700 file:px-3 file:py-2 file:text-white" />
        <button onClick={submit} disabled={busy} data-testid="mat-upload-btn"
          className="flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-bold text-slate-950 hover:bg-amber-400 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Enviar PDF
        </button>
      </div>
    </div>
  );
};

export default function MateriaisPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [materials, setMaterials] = useState([]);
  const [facets, setFacets] = useState({ tipo_concurso: [], banca: [], disciplina: [] });
  const [filter, setFilter] = useState({ banca: "Todas", disciplina: "Todas" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!isAdmin && !user?.is_premium) { navigate("/premium"); return; }
    setLoading(true);
    api.get("/materials", { params: filter })
      .then((r) => { setMaterials(r.data.materials || []); setFacets(r.data.facets || facets); })
      .catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const download = async (m) => {
    if (m.locked) { navigate("/premium"); return; }
    try {
      const res = await api.get(`/materials/${m.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (e.response?.status === 402) navigate("/premium");
      else toast.error("Não foi possível abrir o material.");
    }
  };

  const toggleAtivo = async (m) => {
    try { await api.patch(`/admin/materials/${m.id}`, { ativo: !m.ativo }); load(); } catch { toast.error("Erro."); }
  };
  const remove = async (m) => {
    try { await api.delete(`/admin/materials/${m.id}`); toast.success("Removido."); load(); } catch { toast.error("Erro."); }
  };

  return (
    <div data-testid="materiais-page" className="mx-auto max-w-4xl space-y-5 px-4 py-6 sm:px-6 sm:py-8">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-cyan-500/15 text-cyan-400"><BookOpen className="h-5 w-5" /></span>
        <div>
          <h1 className="text-2xl font-extrabold text-white">Materiais de Estudo</h1>
          <p className="text-sm text-slate-400">Apostilas, resumos e legislação em PDF.</p>
        </div>
      </div>

      {isAdmin && <AdminUpload onDone={load} />}

      <div className="flex flex-wrap gap-2">
        <select value={filter.banca} onChange={(e) => setFilter((f) => ({ ...f, banca: e.target.value }))}
          data-testid="materiais-filter-banca" className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white">
          <option value="Todas">Todas as bancas</option>{facets.banca.map((b) => <option key={b}>{b}</option>)}
        </select>
        <select value={filter.disciplina} onChange={(e) => setFilter((f) => ({ ...f, disciplina: e.target.value }))}
          className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white">
          <option value="Todas">Todas as disciplinas</option>{facets.disciplina.map((d) => <option key={d}>{d}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-emerald-400" /></div>
      ) : materials.length === 0 ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-10 text-center text-slate-400" data-testid="materiais-empty">
          Nenhum material disponível ainda.{isAdmin ? " Envie o primeiro PDF acima." : " Volte em breve!"}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {materials.map((m) => (
            <div key={m.id} data-testid="material-card" className="flex flex-col rounded-2xl border border-slate-800 bg-slate-900 p-5">
              <div className="flex items-start justify-between">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-cyan-500/15 text-cyan-400"><FileText className="h-5 w-5" /></span>
                {m.premium && <span className="flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold text-amber-300"><Lock className="h-3 w-3" /> PREMIUM</span>}
              </div>
              <h3 className="mt-3 font-bold text-white">{m.titulo}</h3>
              {m.descricao && <p className="text-xs text-slate-400">{m.descricao}</p>}
              <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                {[m.banca, m.tipo_concurso, m.disciplina, m.assunto].filter(Boolean).map((t, i) => (
                  <span key={i} className="rounded bg-slate-800 px-2 py-0.5 text-slate-400">{t}</span>
                ))}
              </div>
              <button onClick={() => download(m)} data-testid={`material-download-${m.id}`}
                className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-emerald-500 py-2.5 text-sm font-bold text-white hover:bg-emerald-600">
                {m.locked ? <><Lock className="h-4 w-4" /> Desbloquear</> : <><Download className="h-4 w-4" /> Abrir PDF</>}
              </button>
              {isAdmin && (
                <div className="mt-2 flex gap-2">
                  <button onClick={() => toggleAtivo(m)} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-slate-800 py-1.5 text-xs font-bold text-slate-300 hover:bg-slate-700">
                    {m.ativo ? <><Eye className="h-3.5 w-3.5" /> Ativo</> : <><EyeOff className="h-3.5 w-3.5" /> Inativo</>}
                  </button>
                  <button onClick={() => remove(m)} className="flex items-center justify-center gap-1 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-bold text-red-300 hover:bg-slate-700"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
